import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Home, Phone, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { COMPANY } from "../config/COMPANY-INFO";
import { useCookieConsent } from "../contexts/CookieConsentContext";

const BOOKING_ID_KEY = "apex_last_booking_id";

// Pojistka proti dvojímu odeslání konverze při jednom načtení stránky
// (React StrictMode spouští efekty v dev režimu dvakrát).
let lastConversionAt = 0;

export default function ThankYouPage() {
  const [bookingId, setBookingId] = useState(null);
  const { isAllowed } = useCookieConsent();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    try {
      setBookingId(sessionStorage.getItem(BOOKING_ID_KEY));
    } catch { /* ignore */ }
    // Konverzní značka Google Ads se spustí pouze se souhlasem s marketingem
    // — bez souhlasu skript vůbec nemusí být načtený (viz CookieConsentContext).
    if (!isAllowed("marketing")) return;
    // Vyhrazené místo v <head> pro konverzní kód Google Ads (za hlavní značkou)
    const marker = document.createComment(
      " Event snippet for Odeslání formuláře pro zájemce conversion page "
    );
    document.head.appendChild(marker);
    // Konverzní fragment — vkládá se do <head> POUZE na této stránce a spustí
    // se při každém načtení /dekujeme (SPA i plné načtení).
    if (Date.now() - lastConversionAt < 2000) {
      return () => { try { document.head.removeChild(marker); } catch { /* ignore */ } };
    }
    lastConversionAt = Date.now();
    const conversion = document.createElement("script");
    conversion.setAttribute("data-apex-conversion", "true");
    conversion.text = `
  gtag('event', 'conversion', {
      'send_to': 'AW-18426007268/HjDyCOGQ-uwcEOSdmtJE',
      'value': 1.0,
      'currency': 'CZK'
  });`;
    document.head.appendChild(conversion);
    return () => {
      // Komentář uklidíme, skript v <head> ponecháme (ochrana proti dvojímu
      // odeslání je řešena časovým oknem výše).
      try { document.head.removeChild(marker); } catch { /* ignore */ }
    };
  }, [isAllowed]);

  return (
    <div className="min-h-screen bg-apex-bg text-apex-cream flex flex-col" data-testid="thank-you-page">
      <SEO
        title="Děkujeme za rezervaci"
        description="Děkujeme za vaši žádost o rezervaci. Ozveme se vám do 1 hodiny a doladíme termín."
        path="/dekujeme"
      />
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Navbar />

      <main className="flex-1 relative overflow-hidden pt-32 pb-20">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 1000px 380px at 50% -10%, rgba(229,200,128,0.12), transparent 70%)" }}
        />
        <div className="relative max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl bg-apex-surface border border-apex-line p-7 sm:p-10 text-center"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
              <Check className="w-8 h-8" />
            </div>
            <h1 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-apex-cream">
              Děkujeme za rezervaci
            </h1>
            <p className="mt-4 text-apex-cream/75 text-base leading-relaxed max-w-xl mx-auto">
              Vaši žádost jsme přijali. <strong className="text-apex-gold">Ozveme se vám do 1 hodiny</strong>,
              abychom společně doladili přesný termín a detaily zakázky.
            </p>

            {bookingId && (
              <div
                className="mt-6 inline-block px-5 py-3 rounded-xl bg-apex-card border border-apex-line text-apex-cream/80 text-sm"
                data-testid="thank-you-booking-id"
              >
                ID rezervace: <span className="text-apex-gold font-mono break-all">{bookingId}</span>
              </div>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              <div className="rounded-2xl bg-apex-card border border-apex-line p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-apex-gold flex-shrink-0 mt-0.5" />
                <p className="text-sm text-apex-cream/75 leading-relaxed">
                  Potvrzení žádosti vám právě míří i na e-mail.
                </p>
              </div>
              <div className="rounded-2xl bg-apex-card border border-apex-line p-4 flex items-start gap-3">
                <Phone className="w-5 h-5 text-apex-gold flex-shrink-0 mt-0.5" />
                <p className="text-sm text-apex-cream/75 leading-relaxed">
                  Spěcháte? Volejte{" "}
                  <a href={`tel:${COMPANY.contact.phoneRaw}`} className="text-apex-gold hover:underline" data-testid="thank-you-phone">
                    {COMPANY.contact.phone}
                  </a>
                </p>
              </div>
            </div>

            <div className="mt-8">
              <Link to="/" className="btn-gold inline-flex" data-testid="thank-you-home-link">
                <Home className="w-4 h-4" /> Zpět na hlavní stránku
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
